(function (scope, documentRef) {
  'use strict';

  if (!scope || !documentRef) return;
  if (scope.GCHomeStory && scope.GCHomeStory.version) {
    scope.GCHomeStory.mountAll(documentRef);
    return;
  }

  const instances = new WeakMap();
  const requiredFeatureCount = 5;
  const minimumEnhancedHeight = 560;

  function scrollTargetsFor(element) {
    const targets = new Set([scope]);
    let ancestor = element.parentElement;

    while (ancestor && ancestor !== documentRef.documentElement) {
      const style = scope.getComputedStyle(ancestor);
      if (
        /(auto|scroll|overlay)/.test(style.overflowY) &&
        ancestor.scrollHeight > ancestor.clientHeight
      ) {
        targets.add(ancestor);
      }
      ancestor = ancestor.parentElement;
    }

    const pageWrapper = documentRef.querySelector('.page-wrapper');
    if (pageWrapper) targets.add(pageWrapper);
    return Array.from(targets);
  }

  function scrollNodeFor(target) {
    return target === scope ? documentRef.scrollingElement : target;
  }

  function scrollPosition(target) {
    const node = scrollNodeFor(target);
    return node ? node.scrollTop : 0;
  }

  function setScrollPosition(target, position) {
    const node = scrollNodeFor(target);
    if (!node) return;

    const previousBehavior = node.style.scrollBehavior;
    node.style.scrollBehavior = 'auto';
    node.scrollTop = Math.max(0, position);
    node.style.scrollBehavior = previousBehavior;
  }

  function createSeekQueue(video) {
    let target = 0;
    let disposed = false;

    function flush() {
      if (
        disposed ||
        video.readyState < 2 ||
        !Number.isFinite(video.duration) ||
        video.seeking
      ) {
        return;
      }

      const end = Math.max(0, video.duration - 1 / 24);
      const next = Math.max(0, Math.min(end, target));
      if (Math.abs(video.currentTime - next) > 1 / 48) {
        video.currentTime = next;
      }
    }

    video.addEventListener('seeked', flush);
    video.addEventListener('loadeddata', flush);

    return {
      request(time) {
        target = Number.isFinite(time) ? time : 0;
        flush();
      },
      destroy() {
        disposed = true;
        video.removeEventListener('seeked', flush);
        video.removeEventListener('loadeddata', flush);
      }
    };
  }

  function mountStory(root) {
    if (!root || instances.has(root)) {
      return root ? instances.get(root) : null;
    }

    const stage = root.querySelector('[data-gc-story-stage]');
    const opening = root.querySelector('[data-gc-opening]');
    const openingLink = opening ? opening.querySelector('a[href]') : null;
    const media = root.querySelector('[data-gc-media]');
    const frame = root.querySelector('[data-gc-frame]');
    const video = root.querySelector('[data-gc-video]');
    const full = root.querySelector('[data-gc-full]');
    const heading = root.querySelector('[data-gc-heading]');
    const subhead = root.querySelector('[data-gc-subhead]');
    const features = Array.from(root.querySelectorAll('[data-gc-feature]'));

    if (
      !stage ||
      !opening ||
      !openingLink ||
      !media ||
      !frame ||
      !video ||
      !full ||
      !heading ||
      !subhead ||
      features.length !== requiredFeatureCount
    ) {
      return null;
    }

    const timeline = scope.GCHomeStoryTimeline;
    const motionQuery = scope.matchMedia('(prefers-reduced-motion: reduce)');
    const staticPreview = /(?:[?&])gc_story_static=1(?:&|$)/.test(scope.location ? scope.location.search : '');
    const scrollTargets = scrollTargetsFor(root);
    const inlineNodes = [opening, media, full, heading, subhead, ...features];
    const originalLinkTabindex = openingLink.getAttribute('tabindex');
    const styleCache = new WeakMap();
    const listeners = [];
    let destroyed = false;
    let enhanced = false;
    let activating = false;
    let failed = false;
    let preparing = false;
    let mediaReady = false;
    let selectedSource = '';
    let seekQueue = null;
    let resizeObserver = null;
    let frameRequest = 0;
    let activationRequest = 0;
    let preparationToken = 0;
    let preparationTimer = 0;
    let readyCleanup = null;
    let lastNavigationOffset = null;
    let linkVisible = null;
    let activeScrollTarget = null;
    let lastEnhancedAnchorTop = null;
    let lastEnhancedRootHeight = null;
    let renderedProgress = null;
    let lastFrameTime = 0;

    function listen(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      listeners.push(() => target.removeEventListener(type, handler, options));
    }

    function setStyle(element, property, value) {
      let cache = styleCache.get(element);
      if (!cache) {
        cache = {};
        styleCache.set(element, cache);
      }
      if (cache[property] === value) return;
      cache[property] = value;
      element.style[property] = value;
    }

    function clearInlineState() {
      inlineNodes.forEach((element) => {
        element.style.removeProperty('opacity');
        element.style.removeProperty('transform');
        element.style.removeProperty('pointer-events');
        styleCache.delete(element);
      });
      if (originalLinkTabindex === null) {
        openingLink.removeAttribute('tabindex');
      } else {
        openingLink.setAttribute('tabindex', originalLinkTabindex);
      }
    }

    function navigationOffset() {
      const candidates = Array.from(documentRef.querySelectorAll(
        '#header-group, header-component, .shopify-section-group-header-group'
      ));
      let offset = 0;

      candidates.forEach((element) => {
        const style = scope.getComputedStyle(element);
        if (style.position !== 'fixed' && style.position !== 'sticky') return;

        const rect = element.getBoundingClientRect();
        if (
          rect.height <= 0 ||
          rect.height > scope.innerHeight * 0.35
        ) {
          return;
        }
        // Reserve the sticky navigation's settled height before it reaches
        // the top. Measuring the visible bottom made the stage resize as
        // the announcement scrolled away, moving all percentage-based copy.
        const settledTop = parseFloat(style.top) || 0;
        offset = Math.max(offset, Math.min(scope.innerHeight, rect.height + settledTop));
      });

      return Math.max(0, offset);
    }

    function viewportSupportsEnhancement() {
      return scope.innerHeight >= minimumEnhancedHeight;
    }

    function rootIsPastViewport() {
      return root.getBoundingClientRect().bottom <= navigationOffset();
    }

    function followingAnchor() {
      return root.nextElementSibling ||
        (root.parentElement ? root.parentElement.nextElementSibling : null);
    }

    function captureEnhancedAnchor() {
      if (!enhanced) return;
      const currentRootHeight = root.offsetHeight;
      if (
        Number.isFinite(lastEnhancedRootHeight) &&
        currentRootHeight < lastEnhancedRootHeight * 0.8
      ) {
        return;
      }
      const anchor = followingAnchor();
      if (anchor) lastEnhancedAnchorTop = anchor.getBoundingClientRect().top;
      lastEnhancedRootHeight = currentRootHeight;
    }

    function actualScrollTarget() {
      if (activeScrollTarget) return activeScrollTarget;

      const scrolledTarget = scrollTargets.find(
        (target) => scrollPosition(target) > 0
      );
      return scrolledTarget || scope;
    }

    function openingHasBeenLeft() {
      return scrollTargets.some((target) => scrollPosition(target) > 4) ||
        rootIsPastViewport();
    }

    function updateLinkAccess(opacity) {
      const visible = opacity > 0.05;
      if (visible === linkVisible) return;
      linkVisible = visible;
      setStyle(opening, 'pointerEvents', visible ? 'auto' : 'none');
      if (visible) {
        if (originalLinkTabindex === null) {
          openingLink.removeAttribute('tabindex');
        } else {
          openingLink.setAttribute('tabindex', originalLinkTabindex);
        }
      } else {
        openingLink.setAttribute('tabindex', '-1');
      }
    }

    function update(timestamp) {
      frameRequest = 0;
      if (destroyed || !enhanced || documentRef.visibilityState === 'hidden') {
        return;
      }

      const top = navigationOffset();
      if (top !== lastNavigationOffset) {
        root.style.setProperty('--gc-story-top', `${top}px`);
        lastNavigationOffset = top;
      }
      const rootRect = root.getBoundingClientRect();
      const stageHeight = stage.getBoundingClientRect().height;
      const stageTop = parseFloat(scope.getComputedStyle(stage).top) || 0;
      const travel = Math.max(1, root.offsetHeight - stageHeight);
      const targetProgress = Math.max(0, Math.min(1, (stageTop - rootRect.top) / travel));
      const now = Number.isFinite(timestamp) ? timestamp : scope.performance.now();
      const elapsed = lastFrameTime ? Math.min(64, Math.max(1, now - lastFrameTime)) : 16;
      lastFrameTime = now;
      if (renderedProgress === null) renderedProgress = targetProgress;
      renderedProgress += (targetProgress - renderedProgress) * (1 - Math.exp(-elapsed / 100));
      if (Math.abs(targetProgress - renderedProgress) < 0.0001) renderedProgress = targetProgress;
      const progress = renderedProgress;
      const seekEnd = Number.isFinite(video.duration)
        ? Math.max(0, video.duration - 1 / 24)
        : 0;
      const state = timeline.evaluateStory(progress, seekEnd);

      captureEnhancedAnchor();

      setStyle(opening, 'opacity', String(state.openingOpacity));
      setStyle(
        opening,
        'transform',
        `translate(-50%, ${state.openingShift}vh)`
      );
      setStyle(media, 'opacity', String(state.videoOpacity));
      // Wrapped mobile copy needs at least one full text block plus breathing
      // room between the departing and arriving lines.
      const featureUnit = Math.max(
        scope.innerHeight / 100,
        (Math.max(...features.map((element) => element.offsetHeight)) + 24) / 9
      );
      features.forEach((element, index) => {
        const feature = state.features[index];
        setStyle(element, 'opacity', String(feature.opacity));
        setStyle(element, 'transform', `translateY(${feature.shift * featureUnit}px)`);
      });
      setStyle(full, 'opacity', String(state.posterOpacity));
      setStyle(
        full,
        'transform',
        `translateX(-50%) scale(${state.posterScale})`
      );
      setStyle(heading, 'opacity', String(state.headingOpacity));
      setStyle(subhead, 'opacity', String(state.subheadOpacity));
      updateLinkAccess(state.openingOpacity);

      if (seekQueue) seekQueue.request(state.videoTime);
      if (renderedProgress !== targetProgress) scheduleUpdate();
    }

    function scheduleUpdate() {
      if (destroyed || !enhanced || frameRequest) return;
      frameRequest = scope.requestAnimationFrame(update);
    }

    function stopSeekQueue() {
      if (seekQueue) {
        seekQueue.destroy();
        seekQueue = null;
      }
    }

    function deactivateEnhancement(preserveAnchor) {
      const wasEnhanced = enhanced || root.classList.contains('is-enhanced');
      const anchor = preserveAnchor && wasEnhanced && rootIsPastViewport()
        ? followingAnchor() : null;
      const scrollTarget = anchor ? actualScrollTarget() : null;
      const scrollBefore = scrollTarget ? scrollPosition(scrollTarget) : 0;
      const currentRootHeight = root.offsetHeight;
      const layoutAlreadyCollapsed =
        Number.isFinite(lastEnhancedRootHeight) &&
        currentRootHeight < lastEnhancedRootHeight * 0.8;
      const anchorBefore = anchor
        ? (
          layoutAlreadyCollapsed && Number.isFinite(lastEnhancedAnchorTop)
            ? lastEnhancedAnchorTop
            : anchor.getBoundingClientRect().top
        )
        : null;

      if (activationRequest) {
        scope.cancelAnimationFrame(activationRequest);
        activationRequest = 0;
      }
      if (frameRequest) {
        scope.cancelAnimationFrame(frameRequest);
        frameRequest = 0;
      }
      enhanced = false;
      renderedProgress = null;
      lastFrameTime = 0;
      activating = false;
      stopSeekQueue();
      video.pause();
      root.classList.remove('is-enhanced');
      media.classList.remove('is-video-ready');
      root.style.removeProperty('--gc-story-top');
      lastNavigationOffset = null;
      linkVisible = null;
      clearInlineState();

      if (anchor && Number.isFinite(anchorBefore)) {
        const anchorAfter = anchor.getBoundingClientRect().top;
        setScrollPosition(scrollTarget, scrollBefore + anchorAfter - anchorBefore);
      }
      lastEnhancedAnchorTop = null;
      lastEnhancedRootHeight = null;
    }

    function removeVideoSource() {
      video.pause();
      video.removeAttribute('src');
      video.load();
      mediaReady = false;
      preparing = false;
    }

    function failToStatic() {
      if (destroyed) return;
      failed = true;
      preparationToken += 1;
      if (preparationTimer) scope.clearTimeout(preparationTimer);
      preparationTimer = 0;
      if (readyCleanup) readyCleanup();
      readyCleanup = null;
      deactivateEnhancement(true);
      removeVideoSource();
      root.classList.add('is-static');
    }

    function activateEnhancement() {
      if (destroyed || enhanced || failed || staticPreview || motionQuery.matches ||
          !viewportSupportsEnhancement() || !timeline ||
          typeof timeline.evaluateStory !== 'function') return;

      // Establish the scroll scene synchronously, independently of network
      // readiness. Early scrolling must never abandon the animation.
      root.classList.remove('is-static');
      lastNavigationOffset = navigationOffset();
      root.style.setProperty('--gc-story-top', `${lastNavigationOffset}px`);
      root.classList.add('is-enhanced');
      if (mediaReady) media.classList.add('is-video-ready');
      seekQueue = createSeekQueue(video);
      enhanced = true;
      update();
    }

    function resolveImageReady() {
      if (typeof full.decode === 'function') {
        return full.decode().catch(() => {
          if (full.complete && full.naturalWidth > 0) return undefined;
          throw new Error('Full image decode failed');
        });
      }
      if (full.complete) {
        return full.naturalWidth > 0
          ? Promise.resolve()
          : Promise.reject(new Error('Full image failed'));
      }
      return new Promise((resolve, reject) => {
        full.addEventListener('load', resolve, { once: true });
        full.addEventListener('error', reject, { once: true });
      });
    }

    function prepareMedia() {
      if (
        destroyed ||
        failed ||
        preparing ||
        mediaReady ||
        (staticPreview || motionQuery.matches) ||
        !viewportSupportsEnhancement() ||
        !timeline ||
        typeof timeline.evaluateStory !== 'function'
      ) {
        return;
      }

      if (!selectedSource) {
        const mobile = scope.matchMedia('(max-width: 767px)').matches;
        const preferred = mobile
          ? video.dataset.mobileSrc
          : video.dataset.desktopSrc;
        const alternate = mobile
          ? video.dataset.desktopSrc
          : video.dataset.mobileSrc;
        selectedSource = (preferred || alternate || '').trim();
      }
      if (!selectedSource) { failToStatic(); return; }

      preparing = true;
      const token = ++preparationToken;
      let metadataReady = video.readyState >= 1 && Number.isFinite(video.duration);
      let frameReady = video.readyState >= 2;
      let imageReady = false;
      let settled = false;

      function cleanup() {
        video.removeEventListener('loadedmetadata', onMetadata);
        video.removeEventListener('loadeddata', onFrame);
        video.removeEventListener('error', onError);
        if (preparationTimer) scope.clearTimeout(preparationTimer);
        preparationTimer = 0;
        if (readyCleanup === cleanup) readyCleanup = null;
      }

      function complete() {
        if (
          settled ||
          destroyed ||
          token !== preparationToken ||
          !metadataReady ||
          !frameReady ||
          !imageReady
        ) {
          return;
        }
        settled = true;
        cleanup();
        preparing = false;
        mediaReady = true;
        video.pause();
        activateEnhancement();
        if (enhanced) media.classList.add('is-video-ready');
        scheduleUpdate();
      }

      function onMetadata() {
        metadataReady = Number.isFinite(video.duration) && video.duration >= 0;
        complete();
      }

      function onFrame() {
        frameReady = true;
        complete();
      }

      function onError() {
        if (settled || token !== preparationToken) return;
        settled = true;
        cleanup();
        failToStatic();
      }

      readyCleanup = cleanup;
      video.addEventListener('loadedmetadata', onMetadata);
      video.addEventListener('loadeddata', onFrame);
      video.addEventListener('error', onError);
      // Allow a loading grace period, then use the complete static story.
      // Scrolling itself never causes a fallback.
      preparationTimer = scope.setTimeout(onError, 5000);

      resolveImageReady()
        .then(() => {
          imageReady = true;
          complete();
        })
        .catch(() => { imageReady = true; complete(); });

      video.preload = 'auto';
      video.src = selectedSource;
      video.load();
      if (video.readyState >= 1) onMetadata();
      if (video.readyState >= 2) onFrame();
    }

    function handleEnvironmentChange() {
      if (destroyed) return;

      if (staticPreview || motionQuery.matches) {
        preparationToken += 1;
        if (readyCleanup) readyCleanup();
        readyCleanup = null;
        deactivateEnhancement(true);
        if (video.getAttribute('src')) removeVideoSource();
        root.classList.add('is-static');
        return;
      }

      if (!viewportSupportsEnhancement()) {
        deactivateEnhancement(true);
        root.classList.add('is-static');
        return;
      }

      activateEnhancement();
      if (!mediaReady) prepareMedia();
      scheduleUpdate();
    }

    function handleVideoFailure() {
      if (mediaReady || enhanced) failToStatic();
    }

    function destroy() {
      if (destroyed) return;
      destroyed = true;
      preparationToken += 1;
      if (preparationTimer) scope.clearTimeout(preparationTimer);
      if (readyCleanup) readyCleanup();
      if (resizeObserver) resizeObserver.disconnect();
      listeners.splice(0).forEach((remove) => remove());
      deactivateEnhancement(false);
      removeVideoSource();
      root.classList.remove('is-static');
      instances.delete(root);
    }

    const controller = { destroy };
    instances.set(root, controller);

    scrollTargets.forEach((target) => {
      listen(target, 'scroll', () => {
        activeScrollTarget = target;
        if (enhanced) {
          captureEnhancedAnchor();
        }
        scheduleUpdate();
      }, { passive: true });
    });
    listen(scope, 'resize', handleEnvironmentChange, { passive: true });
    listen(scope, 'pageshow', scheduleUpdate);
    listen(video, 'error', handleVideoFailure);
    listen(documentRef, 'visibilitychange', () => {
      if (documentRef.visibilityState === 'visible') scheduleUpdate();
    });

    if (typeof motionQuery.addEventListener === 'function') {
      listen(motionQuery, 'change', handleEnvironmentChange);
    } else if (typeof motionQuery.addListener === 'function') {
      motionQuery.addListener(handleEnvironmentChange);
      listeners.push(() => motionQuery.removeListener(handleEnvironmentChange));
    }

    if (typeof scope.ResizeObserver === 'function') {
      resizeObserver = new scope.ResizeObserver(scheduleUpdate);
      resizeObserver.observe(stage);
      const header = documentRef.querySelector('#header-group, header-component');
      if (header) resizeObserver.observe(header);
    }

    if (staticPreview || motionQuery.matches || !viewportSupportsEnhancement()) {
      root.classList.add('is-static');
    } else {
      activateEnhancement();
      prepareMedia();
    }

    return controller;
  }

  function mountAll(container) {
    if (!container || typeof container.querySelectorAll !== 'function') return;
    if (container.matches && container.matches('[data-gc-story]')) {
      mountStory(container);
    }
    container.querySelectorAll('[data-gc-story]').forEach(mountStory);
  }

  const api = {
    version: '1.0.0',
    mountAll,
    mountStory
  };
  scope.GCHomeStory = api;

  function boot() {
    mountAll(documentRef);
  }

  if (documentRef.readyState === 'loading') {
    documentRef.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  documentRef.addEventListener('shopify:section:load', (event) => {
    mountAll(event.target);
  });
  documentRef.addEventListener('shopify:section:unload', (event) => {
    const stories = [];
    if (event.target.matches && event.target.matches('[data-gc-story]')) {
      stories.push(event.target);
    }
    if (event.target.querySelectorAll) {
      stories.push(...event.target.querySelectorAll('[data-gc-story]'));
    }
    stories.forEach((story) => {
      const controller = instances.get(story);
      if (controller) controller.destroy();
    });
  });
})(typeof window !== 'undefined' ? window : null, typeof document !== 'undefined' ? document : null);
